import React, { useEffect, useRef, useState } from 'react'
import './SubscribeForm.css'

// Icons
import { AiFillFire } from "react-icons/ai";
import { LuUtilityPole } from "react-icons/lu";
import { BsThreeDots } from "react-icons/bs";
import { IoClose } from "react-icons/io5";
import type { IconType } from "react-icons";

type Industry = 'wildfire' | 'utility' | 'other';

const INDUSTRY_OPTIONS: {
    id: Industry;
    label: string;
    description: string;
    Icon: IconType;
}[] = [
    {
        id: 'wildfire',
        label: 'Wildfire',
        description: 'Fire agencies, land managers, and first responders.',
        Icon: AiFillFire,
    },
    {
        id: 'utility',
        label: 'Utility',
        description: 'Power companies and grid operators.',
        Icon: LuUtilityPole,
    },
    {
        id: 'other',
        label: 'Other',
        description: 'Researchers, partners, and anyone following our work.',
        Icon: BsThreeDots,
    },
];

const SubscribeForm = () => {
    const [email, setEmail] = useState('')
    const [submitted, setSubmitted] = useState(false)
    const [error, setError] = useState('')
    const [selectIndustryPopup, setSelectIndustryPopup] = useState(false);
    const [pendingIndustry, setPendingIndustry] = useState<Industry | null>(null);
    const [popupError, setPopupError] = useState('');

    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const openIndustryPopup = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setPopupError('');
        setSelectIndustryPopup(true);
    };

    const closePopup = () => {
        if (pendingIndustry) return; // don't close mid-request
        setSelectIndustryPopup(false);
        setPopupError('');
    };

    // Escape to close, lock page scroll, and move focus into the dialog
    useEffect(() => {
        if (!selectIndustryPopup) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closePopup();
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);
        closeButtonRef.current?.focus();

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectIndustryPopup, pendingIndustry]);

    // Close when clicking the backdrop (not the dialog itself)
    const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) closePopup();
    };

    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin", // include cookies in the request
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    const subscribeWithIndustry = async (industry: Industry) => {
        if (pendingIndustry) return;

        setPendingIndustry(industry);
        setPopupError('');

        try {
            const csrfToken = await fetchCsrfToken();
            if (!csrfToken) {
                throw new Error("We couldn't reach our server. Check your connection and try again.");
            }

            const response = await fetch('/api/subscribe/', {
                method: 'POST',
                credentials: "same-origin",
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken
                },
                body: JSON.stringify({ email, industry }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Subscription failed. Please try again.');
            }

            setSelectIndustryPopup(false);
            setSubmitted(true);
            setError('');
            setEmail('');

            setTimeout(() => setSubmitted(false), 6000);

        } catch (err: any) {
            console.error(err);
            setPopupError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setPendingIndustry(null);
        }
    };


    return (
        <div className="subscribe-wrapper">
            {submitted ? (
                <div className="thank-you-message">
                    <h1>Thank you for subscribing!</h1>
                </div>
            ) : (
                <form onSubmit={openIndustryPopup} className="my-sub-form">

                    <div className='blog-mini-div'>
                        <h1>
                            The storm moves fast. We move faster.
                            Get updates on the future of detection.
                        </h1>
                    </div>

                    <div className="sub-form-items-and-text">
                        <div className="sub-form-items">
                            <div className="sub-form-block">
                                <input
                                    className="subs-input"
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <button
                                className="subs-submit-button"
                                type="submit"
                            >
                                Send
                            </button>
                        </div>

                        <p>
                            Sign up for monthly updates on our progress,
                            partnerships, and the evolution of
                            High-Risk-Lightning™ detection.
                        </p>
                    </div>

                    {error && <p className="error-message">{error}</p>}
                </form>
            )}

            {/* Industry selection popup */}
            {selectIndustryPopup && (
                <div className="sip-overlay" onMouseDown={handleOverlayMouseDown}>
                    <div
                        className="sip-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="sip-title"
                        aria-describedby="sip-desc"
                    >
                        <div className="sip-header">
                            <button
                                ref={closeButtonRef}
                                type="button"
                                className="sip-close"
                                onClick={closePopup}
                                disabled={pendingIndustry !== null}
                                aria-label="Close"
                            >
                                <IoClose aria-hidden="true" />
                            </button>

                            <h2 id="sip-title">Which industry are you in?</h2>
                            <p id="sip-desc">
                                Pick the one that fits best to finish subscribing{' '}
                                <span className="sip-email">{email}</span>.
                            </p>
                        </div>

                        <div className="sip-body">
                            <div className="sip-options">
                                {INDUSTRY_OPTIONS.map(({ id, label, description, Icon }) => {
                                    const isPending = pendingIndustry === id;

                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            className={`sip-option${isPending ? ' is-pending' : ''}`}
                                            onClick={() => subscribeWithIndustry(id)}
                                            disabled={pendingIndustry !== null}
                                            aria-busy={isPending}
                                        >
                                            <span className="sip-option-icon">
                                                <Icon aria-hidden="true" />
                                            </span>

                                            <span className="sip-option-text">
                                                <span className="sip-option-label">{label}</span>
                                                <span className="sip-option-desc">{description}</span>
                                            </span>

                                            {isPending
                                                ? <span className="sip-spinner" aria-label="Subscribing" />
                                                : <span className="sip-option-chevron" aria-hidden="true" />}
                                        </button>
                                    );
                                })}
                            </div>

                            {popupError && (
                                <p className="sip-error" role="alert">{popupError}</p>
                            )}

                            <p className="sip-disclaimer">
                                 By selecting an industry you consent to receive our monthly FNN™ Newsletter. 
                                 You can unsubscribe at any time using the link at the bottom of any newsletter.
                            </p>
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}

export default SubscribeForm